import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API } from '../App';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { ArrowLeft, BookOpen, Eye, Mail, Lock, DollarSign } from 'lucide-react';
import { toast } from 'sonner';
import { motion } from 'framer-motion';

export default function BookDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [book, setBook] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showRequestModal, setShowRequestModal] = useState(false);

  useEffect(() => {
    const fetchBook = async () => {
      try {
        const response = await axios.get(`${API}/books/${id}`);
        setBook(response.data);
      } catch (error) {
        console.error('Failed to fetch book:', error);
        toast.error('Book not found');
        navigate('/');
      } finally {
        setLoading(false);
      }
    };

    fetchBook();
  }, [id, navigate]);

  const handleRequestCopy = () => {
    const subject = encodeURIComponent(`Request: ${book.title}`);
    const body = encodeURIComponent(
      `Hi,\n\nI'd like to request a copy of "${book.title}" by ${book.author}.\n\nThank you!`
    );

    window.location.href = `mailto:jeansky@protonmail.com?subject=${subject}&body=${body}`;

    toast.success('Opening your email client...');
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gradient-to-br from-[#E8F4F8] via-[#F5F1E8] to-[#D8F3F0]">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#0CAAA6]"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#E8F4F8] via-[#F5F1E8] to-[#D8F3F0]">
      {/* Header */}
      <header className="bg-white/80 backdrop-blur-md border-b border-[#0CAAA6]/20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <Button
            variant="ghost"
            onClick={() => navigate('/')}
            className="text-[#0CAAA6]"
            data-testid="back-button"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Island
          </Button>
        </div>
      </header>

      {/* Content */}
      <main className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <Card className="overflow-hidden border-[#0CAAA6]/20">
            <div className="grid md:grid-cols-2 gap-8 p-8">
              {/* Book Cover */}
              <div className="space-y-4">
                <div className="relative rounded-lg overflow-hidden shadow-2xl">
                  <img
                    src={`${process.env.REACT_APP_BACKEND_URL}${book.cover_url}`}
                    alt={book.title}
                    className="w-full h-auto"
                  />
                </div>
              </div>

              {/* Book Info */}
              <div className="space-y-6">
                <div>
                  <h1 className="text-3xl sm:text-4xl font-bold text-[#1A4D5C] mb-2">
                    {book.title}
                  </h1>
                  <p className="text-lg text-[#7A8286]">by {book.author}</p>
                </div>

                {/* Metadata */}
                <div className="flex items-center gap-4 text-sm text-[#7A8286]">
                  <div className="flex items-center gap-1">
                    <Eye className="w-4 h-4" />
                    <span data-testid="book-read-count">{book.read_count} reads</span>
                  </div>
                  <div className="w-px h-4 bg-[#0CAAA6]/30"></div>
                  <div className="flex items-center gap-1">
                    <BookOpen className="w-4 h-4" />
                    <span>{book.page_count} pages</span>
                  </div>
                  <div className="w-px h-4 bg-[#0CAAA6]/30"></div>
                  <div className="uppercase text-xs font-medium text-[#0CAAA6]">
                    {book.file_type}
                  </div>
                </div>

                {/* Description */}
                <div>
                  <h3 className="font-semibold text-[#1A4D5C] mb-2">About this book</h3>
                  <p className="text-[#7A8286] leading-relaxed">{book.description}</p>
                </div>

                {/* CTAs */}
                <div className="space-y-3">
                  <Button
                    onClick={() => navigate(`/read/${book.id}`)}
                    className="w-full bg-[#0CAAA6] hover:bg-[#0A8883] text-white h-12"
                    data-testid="start-reading-button"
                  >
                    <BookOpen className="w-5 h-5 mr-2" />
                    Start Reading
                  </Button>

                  <Button
                    onClick={() => setShowRequestModal(true)}
                    variant="outline"
                    className="w-full border-[#0CAAA6] text-[#0CAAA6] hover:bg-[#0CAAA6]/10 h-12"
                    data-testid="request-copy-button"
                  >
                    <Mail className="w-5 h-5 mr-2" />
                    Request a Copy
                  </Button>

                  {/* Future Monetization */}
                  {book.price && (
                    <div className="p-4 bg-gradient-to-r from-[#FF6B6B]/10 to-[#0CAAA6]/10 rounded-lg border border-[#0CAAA6]/30">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-sm font-medium text-[#1A4D5C]">Coming Soon</p>
                          <p className="text-xs text-[#7A8286]">Download option</p>
                        </div>
                        <div className="flex items-center gap-2">
                          <Lock className="w-4 h-4 text-[#0CAAA6]" />
                          <span className="text-lg font-bold text-[#0CAAA6]">${book.price}</span>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </Card>
        </motion.div>
      </main>

      {/* Request Modal */}
      <Dialog open={showRequestModal} onOpenChange={setShowRequestModal}>
        <DialogContent className="sm:max-w-md" data-testid="request-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-[#1A4D5C]">
              <span className="text-2xl">🏝️</span>
              Interested in a personal copy?
            </DialogTitle>
            <DialogDescription className="text-[#7A8286]">
              Send me an email request and I'll share the full version with you!
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="p-4 bg-[#F5F1E8] rounded-lg">
              <p className="text-sm text-[#1A4D5C] mb-1">Book: <strong>{book.title}</strong></p>
              <p className="text-sm text-[#7A8286]">Author: {book.author}</p>
            </div>
            <Button
              onClick={handleRequestCopy}
              className="w-full bg-[#0CAAA6] hover:bg-[#0A8883] text-white h-12"
              data-testid="email-button"
            >
              <Mail className="w-5 h-5 mr-2" />
              Email jeansky@protonmail.com
            </Button>
            <p className="text-xs text-center text-[#7A8286]">
              (Opens your email client)
            </p>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}